/**
 * notificationLink.js
 * ===================
 *
 * Notification rows (and their email counterparts) carry a `link` path
 * that was rendered by the backend when the event fired. Two problems
 * to absorb on the FE side:
 *
 *   1. Cross-workspace deep links. Workspace-scoped destinations are
 *      prefixed with `/workspaces/<uid>` so a click on a notification
 *      from workspace B while workspace A is active can switch context
 *      first. The `/workspaces/:workspaceId/*` route (App.js) does the
 *      switch and then opens the inner path.
 *
 *   2. Legacy paths. Notifications are STORED documents — rows written
 *      before a route rename keep the old path forever. `mapLegacyPath`
 *      translates every link shape the backend has ever generated onto
 *      a route that exists in today's router.
 *
 * Used by the WorkspaceDeepLink route and the LegacyPathRedirect alias
 * routes in App.js.
 */

// Real (legacy) routes that live under /foundation/ — a single unknown
// segment after /foundation/ is a pipeline id from an old fine-tune
// notification, but these named pages must keep their own redirects.
const FOUNDATION_PAGES = new Set([
  'marketplace', 'leaderboard', 'external', 'backbones',
]);

/**
 * Split an optional `/workspaces/<uid>` prefix off a link.
 * Returns `{ workspaceId, rest }` where `rest` always starts with '/'
 * and keeps the query string. Links without the prefix come back with
 * `workspaceId: null` and the input untouched.
 */
export const splitWorkspacePrefix = (link) => {
  const m = /^\/workspaces\/([^/?#]+)((?:\/[^?#]*)?)((?:[?#].*)?)$/.exec(link || '');
  if (!m) return { workspaceId: null, rest: link || '/' };
  return { workspaceId: m[1], rest: (m[2] || '/') + (m[3] || '') };
};

/**
 * Map a path (+ optional query string) that may use a legacy /
 * backend-only shape onto a route the router actually serves.
 * Unknown paths pass through unchanged.
 */
export const mapLegacyPath = (pathAndSearch) => {
  const input = pathAndSearch || '/';
  const qIdx = input.search(/[?#]/);
  let path = qIdx === -1 ? input : input.slice(0, qIdx);
  const search = qIdx === -1 ? '' : input.slice(qIdx);

  if (!path.startsWith('/')) path = `/${path}`;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

  // The old "home" link; the dashboard lives at the root route.
  if (path === '/' || path === '/dashboard') return '/';

  // Data sources were briefly linked with a dash the router never had.
  if (path === '/data-sources' || path.startsWith('/data-sources/')) {
    return path.replace('/data-sources', '/datasources') + search;
  }

  // "Data signals" is the Signals page; its causality tab moved to the
  // dedicated Relationships page.
  if (path === '/data-signals') {
    const tab = new URLSearchParams(search.replace(/^[?#]/, '')).get('tab');
    return tab === 'causality' ? '/relationships' : '/signals';
  }

  // Deployment detail is singular (/deployment/<promotionId>).
  const dep = /^\/deployments\/([^/]+)$/.exec(path);
  if (dep) return `/deployment/${dep[1]}${search}`;

  // There is no per-training detail page — land on the trainings list.
  if (/^\/trainings\/[^/]+$/.test(path)) return '/trainings';

  // Old fine-tune notifications linked /foundation/<pipelineId>; the
  // named foundation pages keep their own legacy redirects.
  const fm = /^\/foundation\/([^/]+)$/.exec(path);
  if (fm && !FOUNDATION_PAGES.has(fm[1])) {
    return `/pipeline/${fm[1]}${search}`;
  }

  return path + search;
};

/**
 * Convenience for click handlers: absolute URLs (admin announcements
 * may carry one) must leave the SPA; everything else is an internal
 * path for `navigate()`.
 */
export const isExternalLink = (link) => /^https?:\/\//i.test(link || '');
